import React from 'react';
import { Gavel as RuleIcon } from '@mui/icons-material';
import { Box, Card, CardContent, Divider, Stack, Typography } from '@mui/material';

import type { ComplianceRule } from '../types';

interface ComplianceRuleCardProps {
  rule: ComplianceRule;
}

export const ComplianceRuleCard: React.FC<ComplianceRuleCardProps> = ({ rule }) => {
  return (
    <Card
      sx={{
        width: 300,
        minWidth: 300,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'borderColors.primary',
        boxShadow: 'none',
        overflow: 'hidden',
        transition: 'all 0.2s ease-in-out',
        bgcolor: 'background.paper',
        '&:hover': {
          boxShadow: 1,
          borderColor: 'borderColors.brand',
        },
      }}
    >
      {/* Header Section with Icon */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          p: 1.5,
          pb: 1,
          bgcolor: 'backgroundColors.secondary',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 28,
            height: 28,
            borderRadius: 1,
            bgcolor: 'primary.main',
            color: 'textColors.brandContrast',
            flexShrink: 0,
          }}
        >
          <RuleIcon sx={{ fontSize: 16 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              fontSize: '0.8125rem',
              lineHeight: 1.25,
              color: 'textColors.darkText',
              mb: 0.25,
            }}
          >
            {rule.rule_text}
          </Typography>
          <Typography variant="caption" sx={{ color: 'textColors.lightText', fontSize: '0.6875rem' }}>
            {rule.payer_type === 'specific' ? rule.applicable_payer_name : 'General Requirement'}
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ borderColor: 'borderColors.primary' }} />

      {/* Content Section */}
      <CardContent sx={{ p: 1.5, pt: 1, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
        <Stack spacing={1}>
          {/* Criterion */}
          <Box>
            <Typography
              variant="overline"
              sx={{
                fontWeight: 600,
                color: 'textColors.lightText',
                fontSize: '0.625rem',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
                display: 'block',
                mb: 0.25,
              }}
            >
              Criterion
            </Typography>
            <Typography variant="body2" sx={{ fontSize: '0.75rem', lineHeight: 1.4, color: 'textColors.darkText' }}>
              {rule.criterion}
            </Typography>
          </Box>

          {/* Compliance Rationale */}
          <Box>
            <Typography
              variant="overline"
              sx={{
                fontWeight: 600,
                color: 'textColors.lightText',
                fontSize: '0.625rem',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
                display: 'block',
                mb: 0.25,
              }}
            >
              Rationale
            </Typography>
            <Typography variant="body2" sx={{ fontSize: '0.75rem', lineHeight: 1.4, color: 'textColors.darkText' }}>
              {rule.compliance_rationale}
            </Typography>
          </Box>

          {/* Format or Wording Required */}
          {rule.format_or_wording_required && rule.format_or_wording_required !== 'N/A' && (
            <Box>
              <Typography
                variant="overline"
                sx={{
                  fontWeight: 600,
                  color: 'textColors.lightText',
                  fontSize: '0.625rem',
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase',
                  display: 'block',
                  mb: 0.25,
                }}
              >
                Format Required
              </Typography>
              <Box
                sx={{
                  p: 0.75,
                  borderRadius: 1,
                  bgcolor: 'backgroundColors.tertiary',
                  border: '1px solid',
                  borderColor: 'borderColors.lightprimary',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '0.6875rem',
                    lineHeight: 1.3,
                    fontFamily: 'monospace',
                    color: 'textColors.darkText',
                  }}
                >
                  {rule.format_or_wording_required}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Other Artifacts Required */}
          {rule.other_artifacts_required && rule.other_artifacts_required.length > 0 && (
            <Box>
              <Typography
                variant="overline"
                sx={{
                  fontWeight: 600,
                  color: 'textColors.lightText',
                  fontSize: '0.625rem',
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase',
                  display: 'block',
                  mb: 0.25,
                }}
              >
                Other Artifacts Required
              </Typography>
              <Stack spacing={0.25}>
                {rule.other_artifacts_required.map((artifact, idx) => (
                  <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: '0.6875rem',
                        lineHeight: 1.3,
                        color: 'textColors.brand',
                        fontWeight: 600,
                        mt: 0.125,
                      }}
                    >
                      •
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.6875rem', lineHeight: 1.3, color: 'textColors.darkText', flex: 1 }}>
                      {artifact}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}
        </Stack>
      </CardContent>

      {/* Footer with Source Information */}
      <Box
        sx={{
          px: 1.5,
          py: 1,
          bgcolor: 'backgroundColors.secondary',
          borderTop: '1px solid',
          borderColor: 'borderColors.primary',
        }}
      >
        <Typography variant="caption" sx={{ color: 'textColors.lightText', fontSize: '0.6875rem', fontStyle: 'italic' }}>
          Source: <strong style={{ color: 'inherit' }}>{rule.source_document}</strong>
          {rule.source_date && ` • ${rule.source_date}`}
        </Typography>
      </Box>
    </Card>
  );
};

